import { supabase } from '../../shared/supabase/supabaseClient';
import {
  getCatalogRamSnapshotCache,
  patchWebCatalogStocksBatchInCache,
} from './catalogRamSnapshotCache';

export type RecipeKind = 'assemble' | 'disassemble';

export type RecipeLineDto = {
  id?: string;
  productId: string;
  productName?: string;
  qty: number;
  sortOrder: number;
  isShrinkage: boolean;
};

export type RecipeDto = {
  id: string;
  productId: string;
  productName?: string;
  kind: RecipeKind;
  yieldQty: number;
  name: string | null;
  notes: string | null;
  lines: RecipeLineDto[];
};

export type RecipeUpsertPayload = {
  productId: string;
  kind: RecipeKind;
  yieldQty: number;
  name?: string | null;
  notes?: string | null;
  branchId?: string | null;
  lines: Array<{
    productId: string;
    qty: number;
    sortOrder?: number;
    isShrinkage?: boolean;
  }>;
};

export type RecipeProducePayload = {
  batchId: string;
  branchId: string;
  productId: string;
  batchQty: number;
  performedByUserId?: string | null;
  deviceId?: string | null;
};

function asRecipe(raw: unknown): RecipeDto | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const linesRaw = Array.isArray(o.lines) ? o.lines : [];
  return {
    id: String(o.id),
    productId: String(o.productId),
    productName: o.productName != null ? String(o.productName) : undefined,
    kind: (o.kind === 'disassemble' ? 'disassemble' : 'assemble') as RecipeKind,
    yieldQty: Number(o.yieldQty ?? 1),
    name: o.name != null ? String(o.name) : null,
    notes: o.notes != null ? String(o.notes) : null,
    lines: linesRaw.map((line, idx) => {
      const l = line as Record<string, unknown>;
      return {
        id: l.id != null ? String(l.id) : undefined,
        productId: String(l.productId),
        productName: l.productName != null ? String(l.productName) : undefined,
        qty: Number(l.qty ?? 0),
        sortOrder: Number(l.sortOrder ?? idx),
        isShrinkage: Boolean(l.isShrinkage),
      };
    }),
  };
}

export async function getRecipeForProduct(
  productId: string,
  kind: RecipeKind = 'assemble',
): Promise<RecipeDto | null> {
  const { data, error } = await supabase.rpc('recipes_get_for_product', {
    p_product_id: productId,
    p_kind: kind,
  });
  if (error) throw error;
  const payload = data as { ok?: boolean; recipe?: unknown } | null;
  return asRecipe(payload?.recipe ?? null);
}

export async function upsertRecipe(payload: RecipeUpsertPayload): Promise<{ recipeId: string }> {
  const { data, error } = await supabase.rpc('recipes_upsert_recipe', {
    p_payload: {
      productId: payload.productId,
      kind: payload.kind,
      yieldQty: payload.yieldQty,
      name: payload.name ?? null,
      notes: payload.notes ?? null,
      branchId: payload.branchId ?? null,
      lines: payload.lines.map((l, i) => ({
        productId: l.productId,
        qty: l.qty,
        sortOrder: l.sortOrder ?? i,
        isShrinkage: l.isShrinkage ?? false,
      })),
    },
  });
  if (error) throw error;
  const result = data as { recipeId?: string };
  return { recipeId: String(result.recipeId ?? '') };
}

/**
 * Stock actual del producto en la sucursal según el snapshot RAM (ADR-017).
 * Sin fila → 0 (el server crea la fila en 0 al aplicar el ajuste).
 */
export function getCachedBranchProductQty(productId: string, branchId: string): number {
  const stocks = getCatalogRamSnapshotCache()?.stocks ?? [];
  const row = stocks.find((s) => {
    if (s.product_id !== productId || s.branch_id !== branchId) return false;
    const variantId = (s as { variant_id?: string | null }).variant_id;
    return variantId == null || variantId === '';
  });
  return Number(row?.quantity ?? 0);
}

/**
 * El RPC ya movió stock en servidor. Sin este patch, el snapshot RAM (ADR-017)
 * queda viejo hasta F5 / sync remoto — Catálogo e Inventario leen de ahí.
 */
function readCachedParentQty(productId: string, branchId: string): number {
  return getCachedBranchProductQty(productId, branchId);
}

function patchLocalStocksAfterRecipeBatch(params: {
  kind: RecipeKind;
  branchId: string;
  productId: string;
  batchQty: number;
  recipe: RecipeDto;
}): void {
  const yieldQty = params.recipe.yieldQty > 0 ? params.recipe.yieldQty : 1;
  const scale = params.batchQty / yieldQty;
  const patches: Array<{ productId: string; branchId: string; quantity: number }> = [];

  if (params.kind === 'assemble') {
    for (const line of params.recipe.lines) {
      patches.push({
        productId: line.productId,
        branchId: params.branchId,
        quantity: readCachedParentQty(line.productId, params.branchId) - line.qty * scale,
      });
    }
    patches.push({
      productId: params.productId,
      branchId: params.branchId,
      quantity: readCachedParentQty(params.productId, params.branchId) + params.batchQty,
    });
  } else {
    patches.push({
      productId: params.productId,
      branchId: params.branchId,
      quantity: readCachedParentQty(params.productId, params.branchId) - params.batchQty,
    });
    for (const line of params.recipe.lines) {
      patches.push({
        productId: line.productId,
        branchId: params.branchId,
        quantity: readCachedParentQty(line.productId, params.branchId) + line.qty * scale,
      });
    }
  }

  patchWebCatalogStocksBatchInCache(patches);
}

export async function applyRecipeProduce(payload: RecipeProducePayload): Promise<void> {
  const { error } = await supabase.rpc('sync_apply_recipe_produce', {
    p_payload: {
      batchId: payload.batchId,
      branchId: payload.branchId,
      productId: payload.productId,
      batchQty: payload.batchQty,
      performedByUserId: payload.performedByUserId ?? null,
      deviceId: payload.deviceId ?? null,
    },
  });
  if (error) throw error;
  const recipe = await getRecipeForProduct(payload.productId, 'assemble');
  if (recipe) {
    patchLocalStocksAfterRecipeBatch({
      kind: 'assemble',
      branchId: payload.branchId,
      productId: payload.productId,
      batchQty: payload.batchQty,
      recipe,
    });
  }
}

export async function applyRecipeDisassemble(payload: RecipeProducePayload): Promise<void> {
  const { error } = await supabase.rpc('sync_apply_recipe_disassemble', {
    p_payload: {
      batchId: payload.batchId,
      branchId: payload.branchId,
      productId: payload.productId,
      batchQty: payload.batchQty,
      performedByUserId: payload.performedByUserId ?? null,
      deviceId: payload.deviceId ?? null,
    },
  });
  if (error) throw error;
  const recipe = await getRecipeForProduct(payload.productId, 'disassemble');
  if (recipe) {
    patchLocalStocksAfterRecipeBatch({
      kind: 'disassemble',
      branchId: payload.branchId,
      productId: payload.productId,
      batchQty: payload.batchQty,
      recipe,
    });
  }
}
